// src/components/student/StudentBulkStatus.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Stack,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Preview as PreviewIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  PersonAdd as ActivateIcon,
  PersonRemove as DeactivateIcon,
} from '@mui/icons-material';
import { academicAPI, studentAPI } from '../../services/api';
import { usePermissions, RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import PermissionGuard from '../access-control/PermissionGuard';

const StudentBulkStatus = () => {
  const [classStreams, setClassStreams] = useState([]);
  const [selectedClassStream, setSelectedClassStream] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [targetStatus, setTargetStatus] = useState(true); // true for active, false for inactive
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchClassStreams();
  }, []);

  const fetchClassStreams = async () => {
    try {
      const response = await academicAPI.getClassStreams();
      setClassStreams(response.data || []);
    } catch (error) {
      console.error('Error fetching class streams:', error);
      setSnackbar({ open: true, message: 'Failed to load class streams', severity: 'error' });
    }
  };

  const fetchStudentsForClass = async (classStreamId) => {
    if (!classStreamId) return;
    
    try {
      setLoading(true);
      const response = await studentAPI.getAll({ class_stream_id: classStreamId, per_page: 1000 });
      const studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setStudents(studentsData);
      // Auto-select all students
      setSelectedStudents(new Set(studentsData.map(s => s.id)));
    } catch (error) {
      console.error('Error fetching students:', error);
      setSnackbar({ open: true, message: 'Failed to load students', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClassStreamChange = (classStreamId) => {
    setSelectedClassStream(classStreamId);
    setStudents([]);
    setSelectedStudents(new Set());
    
    if (classStreamId) {
      fetchStudentsForClass(classStreamId);
    }
  };

  const handleStudentToggle = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedStudents.size === students.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(students.map(s => s.id)));
    }
  };

  const handlePreview = () => {
    if (selectedStudents.size === 0) {
      setSnackbar({ open: true, message: 'Please select at least one student', severity: 'warning' });
      return;
    }
    setShowPreview(true);
  };

  const handleExecuteStatusUpdate = async () => {
    const studentIds = Array.from(selectedStudents);

    try {
      setUpdating(true);
      const response = await studentAPI.bulkUpdateStatus({
        student_ids: studentIds,
        active: targetStatus
      });
      
      setSnackbar({ 
        open: true, 
        message: response.data.message || `Successfully ${targetStatus ? 'activated' : 'deactivated'} ${studentIds.length} student(s)`, 
        severity: 'success' 
      });
      
      setShowPreview(false);
      // Refresh the students list
      if (selectedClassStream) {
        fetchStudentsForClass(selectedClassStream);
      }
    } catch (error) {
      console.error('Error updating student status:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to update student status', 
        severity: 'error' 
      });
    } finally {
      setUpdating(false);
    }
  };

  const getClassStreamName = (classStreamId) => {
    const classStream = classStreams.find(cs => cs.id === parseInt(classStreamId));
    return classStream?.display_name || `${classStream?.school_class?.name}-${classStream?.stream?.name}` || 'Unknown';
  };

  const selectedCount = selectedStudents.size;
  const selectedStudentsList = students.filter(s => selectedStudents.has(s.id));
  const activeCount = selectedStudentsList.filter(s => s.active).length;
  const inactiveCount = selectedStudentsList.filter(s => !s.active).length;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Bulk Student Status Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Activate or deactivate multiple students at once
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <Select
                  value={selectedClassStream}
                  label="Select Class Stream"
                  onChange={(e) => handleClassStreamChange(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Select Class Stream</MenuItem>
                  {classStreams.map((classStream) => (
                    <MenuItem key={classStream.id} value={classStream.id}>
                      {classStream.display_name || `${classStream.school_class?.name}-${classStream.stream?.name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {students.length > 0 && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={targetStatus}
                        onChange={(e) => setTargetStatus(e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Target Status: {targetStatus ? 'Active' : 'Inactive'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {targetStatus ? 'Students will be activated' : 'Students will be deactivated'}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="end"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreview}
                      disabled={loading || selectedCount === 0}
                    >
                      Preview ({selectedCount})
                    </Button>
                  </Stack>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>Loading students...</Typography>
        </Box>
      )}

      {students.length > 0 && !loading && (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {students.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Students
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {selectedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selected
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {activeCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active (Selected)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="error.main">
                      {inactiveCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactive (Selected)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Students in {getClassStreamName(selectedClassStream)}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {selectedCount} of {students.length} selected
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    variant={selectedCount === students.length ? "outlined" : "contained"}
                  >
                    {selectedCount === students.length ? "Deselect All" : "Select All"}
                  </Button>
                </Stack>
              </Box>

              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedCount > 0 && selectedCount < students.length}
                          checked={selectedCount === students.length && students.length > 0}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Registration No</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Current Status</TableCell>
                      <TableCell>Guardian</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow 
                        key={student.id} 
                        hover
                        sx={{
                          opacity: student.active ? 1 : 0.7,
                          backgroundColor: student.active ? 'inherit' : 'action.hover'
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedStudents.has(student.id)}
                            onChange={() => handleStudentToggle(student.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary" fontWeight="medium">
                            {student.registration_no || student.lin}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {student.full_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.gender}
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.active ? "Active" : "Inactive"}
                            size="small"
                            color={student.active ? "success" : "error"}
                            variant={student.active ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {student.guardian_name}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Status Update Preview
            </Typography>
            <IconButton onClick={() => setShowPreview(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                You are about to {targetStatus ? 'activate' : 'deactivate'} {selectedCount} student(s).
                {!targetStatus && ' Inactive students will not appear or affect any further records except only to display as inactive students in the students dashboard .'}
              </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2} bgcolor="success.light" borderRadius={1}>
                    <Typography variant="h4" color="success.contrastText">
                      {targetStatus ? selectedCount : 0}
                    </Typography>
                    <Typography variant="body2" color="success.contrastText">
                      Will be Active
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2} bgcolor="error.light" borderRadius={1}>
                    <Typography variant="h4" color="error.contrastText">
                      {!targetStatus ? selectedCount : 0}
                    </Typography>
                    <Typography variant="body2" color="error.contrastText">
                      Will be Inactive
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Registration No</TableCell>
                    <TableCell>Current Status</TableCell>
                    <TableCell>New Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedStudentsList.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {student.full_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {student.registration_no || student.lin}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={student.active ? "Active" : "Inactive"}
                          size="small"
                          color={student.active ? "success" : "error"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={targetStatus ? "Active" : "Inactive"}
                          size="small"
                          color={targetStatus ? "success" : "error"}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        {student.active === targetStatus ? (
                          <Chip label="No Change" size="small" variant="outlined" />
                        ) : (
                          <Box display="flex" alignItems="center">
                            {targetStatus ? (
                              <ActivateIcon color="success" fontSize="small" />
                            ) : (
                              <DeactivateIcon color="error" fontSize="small" />
                            )}
                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                              {targetStatus ? 'Activate' : 'Deactivate'}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowPreview(false)} disabled={updating}>
            Cancel
          </Button>
          <PermissionGuard resource={RESOURCES.STUDENTS} action={ACTIONS.UPDATE}>
            <Button
              variant="contained"
              startIcon={updating ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleExecuteStatusUpdate}
              disabled={updating}
              color={targetStatus ? "success" : "error"}
            >
              {updating ? 'Updating...' : `${targetStatus ? 'Activate' : 'Deactivate'} ${selectedCount} Student(s)`}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentBulkStatus;